import { put, call, all, takeEvery, takeLatest } from 'redux-saga/effects'
import { store as reduxStore } from 'index'
import { getAppHost } from 'helpers'
import {
  listUniqueConversations,
  listConversationMessages,
  listFlaggedMessages,
  listMessageReactions,
  getMessageReceipts,
  listMessageThread,
  getConversation,
} from 'services/conversations'
import { members as fetchGroupMembers } from 'services/groups'
import constants from '../../constants'
import actions from './actions'
import { ConversationExplorerState } from './reducers'

/**
 * Conversation Explorer sagas — read-only. Left list (ENG-36887) + center thread
 * (ENG-36888). Reads run silently (loading flags only), matching the repo's other
 * list sagas; the Explorer is a browse tool, so no success toasts.
 */

// The root store is a plain-JS module (untyped). Read its slices through this
// boundary so the rest of the file stays typed. `apps` is untyped legacy state.
interface RootState {
  apps: { active: any; appSettings: any }
  conversationExplorer: ConversationExplorerState
}
const getRoot = (): RootState => reduxStore.getState() as unknown as RootState

// Every saga is dispatched with `{ type, payload }`; only the payload varies.
type Saga<P> = { payload?: P }

// redux-saga@1's take* overloads infer the action type from the worker's param;
// a param narrowed to `{ payload }` doesn't satisfy their `A extends Action`
// constraint, so they fall back to the channel overload and reject the string
// pattern ("string not assignable to TakeableChannel"). We wire workers through
// this type at the registration boundary — runtime is unchanged and the
// pattern→worker mapping is covered by sagas.test.ts.
type SagaWorker = (action: any) => any

// ENG-36887 — list unique conversations (initial / filter / load-more)
interface ListConversationsPayload {
  cursor?: string
  conversationType?: 'user' | 'group'
  append?: boolean
}
export function* LIST_CONVERSATIONS({ payload = {} }: Saga<ListConversationsPayload>) {
  yield put({ type: actions.SET_STATE, payload: { areConversationsLoading: true } })

  const { active: activeApp, appSettings } = getRoot().apps
  const { cursor, conversationType, append = false } = payload

  try {
    const success = yield call(listUniqueConversations, activeApp, getAppHost(appSettings), {
      cursor,
      conversationType,
    })

    if (success && success.data) {
      const existing = getRoot().conversationExplorer.conversationList
      yield put({
        type: actions.SET_STATE,
        payload: {
          conversationList: append ? [...existing, ...success.data] : success.data,
          // The API returns the cursor flat on `meta` (`meta.next` / `meta.previous`),
          // NOT under `meta.cursor` — same shape as the messages endpoint. Store the
          // whole meta so ConversationList can read `cursor.next` for load-more.
          conversationListCursor: success.meta || {},
          areConversationsLoading: false,
        },
      })
    } else {
      yield put({ type: actions.SET_STATE, payload: { areConversationsLoading: false } })
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areConversationsLoading: false } })
  }
}

// ENG-36888 — list messages for a conversation.
// The API's default page is the NEWEST messages; `meta.previous` (affix=prepend)
// loads OLDER, `meta.next` (affix=append) loads NEWER. The thread starts at the
// newest and pages OLDER as the user scrolls up (prepend).
interface ListMessagesPayload {
  conversationId?: string
  id?: string
  sentAt?: number
  affix?: 'append' | 'prepend'
  searchKey?: string
  append?: boolean
  prepend?: boolean
}
export function* LIST_MESSAGES({ payload = {} }: Saga<ListMessagesPayload>) {
  const { conversationId, id, sentAt, affix, searchKey, append = false, prepend = false } = payload
  if (!conversationId) return

  yield put({ type: actions.SET_STATE, payload: { areMessagesLoading: true } })

  const { active: activeApp, appSettings } = getRoot().apps

  try {
    const success = yield call(
      listConversationMessages,
      activeApp,
      getAppHost(appSettings),
      conversationId,
      { id, sentAt, affix, searchKey },
    )

    if (success && success.data) {
      const state = getRoot().conversationExplorer
      const existing = state.messageList
      let messageList = success.data
      if (prepend) messageList = [...success.data, ...existing]
      else if (append) messageList = [...existing, ...success.data]

      // The API returns BOTH cursors (`previous` = older, `next` = newer). A paged
      // load must only update the side it walked, or it would clobber the other and
      // strand the thread (e.g. prepending would erase the "load newer" cursor and
      // leave an anchored view unable to scroll down). An empty page means that side
      // is exhausted → drop its cursor so we stop asking.
      const meta = success.meta || {}
      const exhausted = success.data.length === 0
      let messageListCursor = meta
      if (prepend) {
        messageListCursor = { ...state.messageListCursor, previous: exhausted ? undefined : meta.previous }
      } else if (append) {
        messageListCursor = { ...state.messageListCursor, next: exhausted ? undefined : meta.next }
      } else if (!sentAt) {
        // Initial NEWEST-page open (no anchor): there is nothing newer, but the API
        // still returns a `next` cursor. Drop it — otherwise the auto scroll-to-bottom
        // fires "load newer", appends messages below the view, and the conversation
        // lands mid-thread instead of at the latest message. An ANCHORED open (has
        // `sentAt`) keeps `next`, since there really are newer messages below the target.
        messageListCursor = { ...meta, next: undefined }
      }

      yield put({
        type: actions.SET_STATE,
        payload: { messageList, messageListCursor, areMessagesLoading: false },
      })
    } else {
      yield put({ type: actions.SET_STATE, payload: { areMessagesLoading: false } })
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areMessagesLoading: false } })
  }
}

// Selecting a conversation clears the thread + right-panel supporting data,
// then loads its first page of messages
interface SelectConversationPayload {
  conversationId?: string
}
export function* SELECT_CONVERSATION({ payload = {} }: Saga<SelectConversationPayload>) {
  const { conversationId } = payload
  // Capture the conversation object now, while it's still in the (unfiltered) list —
  // the header + right panel read `selectedConversation` so they keep showing the
  // name/members after a type filter drops this row from `conversationList`.
  const selected =
    getRoot().conversationExplorer.conversationList.find(
      c => c.conversationId === conversationId,
    ) || null
  yield put({
    type: actions.SET_STATE,
    payload: {
      selectedConversationId: conversationId,
      selectedConversation: selected,
      selectedMessageId: '',
      messageList: [],
      messageListCursor: {},
      groupMembers: [],
      flaggedMessages: [],
      // Thread replies are cached per parent id — drop them so a conversation
      // switch can't leave stale replies behind for the next one.
      threadReplies: {},
      threadLoadingId: '',
    },
  })
  if (!conversationId) return
  // Freshen this row's (possibly stale) left-panel preview from the current
  // lastMessage — fire-and-forget so it never delays the thread load.
  yield put({ type: actions.REFRESH_CONVERSATION_ROW, payload: { conversationId } })
  yield call(LIST_MESSAGES, { payload: { conversationId } })
  // Conversation-wide flagged list → the thread renders an amber "flagged" bubble
  // for every user-flagged message (join by id), and the right panel reuses it.
  yield call(FETCH_FLAGGED_MESSAGES, { payload: { conversationId } })
  // Eagerly load group members so the thread can show each sender's role badge
  // (Admin / Moderator / Member) — the role isn't carried on the message itself.
  const conv = getRoot().conversationExplorer.conversationList.find(
    c => c.conversationId === conversationId,
  )
  if (conv && conv.conversationType === 'group' && conv.group && conv.group.guid) {
    yield call(FETCH_GROUP_MEMBERS, { payload: { guid: conv.group.guid } })
  }
}

interface SelectMessagePayload {
  messageId?: string
}
export function* SELECT_MESSAGE({ payload = {} }: Saga<SelectMessagePayload>) {
  const messageId = payload.messageId || ''

  // Read the message up front so we can flip the panel loading flags in the SAME
  // update that clears the old data — otherwise the panels briefly render their
  // empty state ("No reactors") before the fetch turns loading on.
  const ce = getRoot().conversationExplorer
  const msg =
    ce.messageList.find(m => m.id === messageId) ||
    Object.values(ce.threadReplies || {})
      .flat()
      .find(m => m.id === messageId)
  const hasReactions = !!(msg && msg.data && msg.data.reactions && msg.data.reactions.length)

  // Clear the previous message's per-message panels + go straight to loading. Keep
  // `flaggedMessages` (conversation-wide, loaded on SELECT_CONVERSATION) — the
  // thread needs it for amber bubbles; we only merge the selected message below.
  yield put({
    type: actions.SET_STATE,
    payload: {
      selectedMessageId: messageId,
      messageReactors: [],
      messageReceipts: [],
      areReactorsLoading: hasReactions,
      areReceiptsLoading: Boolean(messageId),
    },
  })
  if (!messageId) return

  // Delivery/read status lives ONLY in per-recipient receipts — the message object
  // carries no deliveredAt/readAt — so fetch them eagerly for the Delivery row +
  // Read Receipts tabs.
  yield call(FETCH_MESSAGE_RECEIPTS, { payload: { messageId } })

  // Reactors — fetch eagerly when the message has reactions, so re-selecting the
  // SAME message refetches (a component effect keyed on message.id wouldn't re-run).
  if (hasReactions) {
    yield call(FETCH_MESSAGE_REACTIONS, { payload: { messageId } })
  }

  // Flag data (Reason / Reported By) isn't on the message either — fetch it per
  // message so it's always current, instead of relying on a cached, paginated,
  // once-per-conversation list that can miss recently-flagged messages.
  const { selectedConversationId } = getRoot().conversationExplorer
  if (selectedConversationId) {
    yield call(FETCH_FLAGGED_MESSAGES, {
      payload: { conversationId: selectedConversationId, messageId, merge: true },
    })
  }
}

// Right panel — Members accordion (lazy). GET /groups/{guid}/members
interface FetchGroupMembersPayload {
  guid?: string
  searchText?: string
  page?: number
  append?: boolean
}
export function* FETCH_GROUP_MEMBERS({ payload = {} }: Saga<FetchGroupMembersPayload>) {
  const { guid, searchText = '', page = 1, append = false } = payload
  if (!guid) return

  yield put({ type: actions.SET_STATE, payload: { areMembersLoading: true } })

  const { active: activeApp, appSettings } = getRoot().apps

  try {
    const success = yield call(fetchGroupMembers, guid, activeApp, getAppHost(appSettings), page, searchText)
    const pageMembers = (success && success.data) || []
    // append mode grows the loaded list (load-more); page 1 replaces it.
    const existing = append ? getRoot().conversationExplorer.groupMembers : []
    yield put({
      type: actions.SET_STATE,
      payload: {
        groupMembers: [...existing, ...pageMembers],
        membersPage: page,
        // a full page implies there may be another; a short page is the last one.
        membersHasMore: pageMembers.length === constants.perPageInListings,
        areMembersLoading: false,
      },
    })
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areMembersLoading: false } })
  }
}

// GET /moderation/flagged-messages. Two callers:
//  • SELECT_CONVERSATION (no messageId) — the conversation-wide list that both the
//    thread (amber flagged bubbles) and the right panel join by message id.
//  • SELECT_MESSAGE (messageId + merge) — freshens/adds just the selected message's
//    record without dropping the conversation-wide list.
interface FetchFlaggedPayload {
  conversationId?: string
  messageId?: string
  merge?: boolean
}
export function* FETCH_FLAGGED_MESSAGES({ payload = {} }: Saga<FetchFlaggedPayload>) {
  const { conversationId, messageId, merge = false } = payload
  if (!conversationId) return

  yield put({ type: actions.SET_STATE, payload: { areFlaggedLoading: true } })

  const { active: activeApp, appSettings } = getRoot().apps

  try {
    const success = yield call(listFlaggedMessages, activeApp, getAppHost(appSettings), {
      conversationId,
      messageId,
    })
    const fetched = (success && success.data) || []
    let flaggedMessages = fetched
    if (merge) {
      const existing = getRoot().conversationExplorer.flaggedMessages || []
      const byId = new Map(existing.map(r => [String(r.id), r]))
      fetched.forEach((r: any) => byId.set(String(r.id), r))
      flaggedMessages = Array.from(byId.values())
    }
    yield put({
      type: actions.SET_STATE,
      payload: { flaggedMessages, areFlaggedLoading: false },
    })
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areFlaggedLoading: false } })
  }
}

// Right panel — Reactions accordion (lazy). GET /messages/{id}/reactions —
// individual reactors (uid + reactedBy entity + emoji), which the message's
// aggregated `data.reactions` (emoji + count only) doesn't carry.
interface MessageIdPayload {
  messageId?: string
}
export function* FETCH_MESSAGE_REACTIONS({ payload = {} }: Saga<MessageIdPayload>) {
  const { messageId } = payload
  if (!messageId) return

  yield put({ type: actions.SET_STATE, payload: { areReactorsLoading: true } })

  const { active: activeApp, appSettings } = getRoot().apps

  try {
    const success = yield call(listMessageReactions, activeApp, getAppHost(appSettings), messageId, {})
    yield put({
      type: actions.SET_STATE,
      payload: {
        messageReactors: (success && success.data) || [],
        areReactorsLoading: false,
      },
    })
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areReactorsLoading: false } })
  }
}

// Right — per-member read/delivered receipts for a group message (lazy, on the
// Read Receipts accordion). GET /messages/{id} → data.receipts.data[].
export function* FETCH_MESSAGE_RECEIPTS({ payload = {} }: Saga<MessageIdPayload>) {
  const { messageId } = payload
  if (!messageId) return

  yield put({ type: actions.SET_STATE, payload: { areReceiptsLoading: true } })

  const { active: activeApp, appSettings } = getRoot().apps

  try {
    const success = yield call(getMessageReceipts, activeApp, getAppHost(appSettings), messageId)
    const receipts = (success && success.data && success.data.receipts && success.data.receipts.data) || []
    yield put({
      type: actions.SET_STATE,
      payload: { messageReceipts: receipts, areReceiptsLoading: false },
    })
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areReceiptsLoading: false } })
  }
}

// Deep-link from the logs pages — open a conversation anchored on a specific
// message. Loads the page that INCLUDES the target (everything at/older than its
// sentAt, target as the newest row), selects it, and flags it for the thread to
// scroll to. Falls back to the newest page when no sentAt is supplied.
interface OpenAtMessagePayload {
  conversationId?: string
  messageId?: string
  sentAt?: number | string
}
export function* OPEN_AT_MESSAGE({ payload = {} }: Saga<OpenAtMessagePayload>) {
  const { conversationId, messageId, sentAt } = payload
  if (!conversationId || !messageId) return

  const selected =
    getRoot().conversationExplorer.conversationList.find(
      c => c.conversationId === conversationId,
    ) || null
  yield put({
    type: actions.SET_STATE,
    payload: {
      selectedConversationId: conversationId,
      selectedConversation: selected,
      selectedMessageId: messageId,
      pendingScrollMessageId: messageId,
      messageList: [],
      messageListCursor: {},
      groupMembers: [],
      flaggedMessages: [],
      messageReactors: [],
      messageReceipts: [],
    },
  })

  // Load a WINDOW around the target, not just one side of it.
  //
  // `sentAt + 1` pulls the older-than page, so the target lands as its newest row.
  // On its own that's not enough: if the target is the conversation's first message
  // there is nothing older, so the thread would show exactly one message with no way
  // to scroll — so we also pull the newer-than page to give it context below.
  const anchor = sentAt ? { sentAt: Number(sentAt) + 1, affix: 'prepend' as const } : {}
  yield call(LIST_MESSAGES, { payload: { conversationId, ...anchor } })
  if (sentAt) {
    yield call(LIST_MESSAGES, {
      payload: {
        conversationId,
        id: messageId,
        sentAt: Number(sentAt),
        affix: 'append',
        append: true,
      },
    })
  }
  yield call(FETCH_MESSAGE_RECEIPTS, { payload: { messageId } })

  // Group members for the sender role badges — from the loaded conversation if
  // present, else derive the guid from a `group_<guid>` conversation id.
  const state = getRoot().conversationExplorer
  const conv = state.conversationList.find(c => c.conversationId === conversationId)
  let guid = conv && conv.group && conv.group.guid
  if (!guid && conversationId.indexOf('group_') === 0) guid = conversationId.slice('group_'.length)
  if (guid) yield call(FETCH_GROUP_MEMBERS, { payload: { guid } })
}

// Center — thread replies of a parent (lazy, on "N Replies" expand).
// GET /messages/{id}/thread. Stored keyed by parent id so several threads can be
// open at once; the replies are hidden from the main timeline via `hideReplies`.
export function* FETCH_MESSAGE_THREAD({ payload = {} }: Saga<MessageIdPayload>) {
  const { messageId } = payload
  if (!messageId) return

  yield put({ type: actions.SET_STATE, payload: { threadLoadingId: messageId } })

  const { active: activeApp, appSettings } = getRoot().apps

  try {
    const success = yield call(listMessageThread, activeApp, getAppHost(appSettings), messageId, {})
    const existing = getRoot().conversationExplorer.threadReplies
    yield put({
      type: actions.SET_STATE,
      payload: {
        threadReplies: { ...existing, [messageId]: (success && success.data) || [] },
        threadLoadingId: '',
      },
    })
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { threadLoadingId: '' } })
  }
}

// Left panel — freshen a single row's `lastMessage`. The conversation list is loaded
// once, so a row's preview goes stale as new messages arrive. GET /conversations/{id}
// returns the CURRENT lastMessage; we patch just that row (no re-sort, no re-fetch of
// the whole list). Fired on select, so opening a conversation snaps its left-panel
// preview to match the freshly-loaded center panel.
interface RefreshRowPayload {
  conversationId?: string
}
export function* REFRESH_CONVERSATION_ROW({ payload = {} }: Saga<RefreshRowPayload>) {
  const { conversationId } = payload
  if (!conversationId) return

  const { active: activeApp, appSettings } = getRoot().apps

  try {
    const success = yield call(getConversation, activeApp, getAppHost(appSettings), conversationId)
    // One entry per participant/member — all carry the same conversationId + lastMessage.
    const fresh = success && Array.isArray(success.data) ? success.data[0] : undefined
    const lastMessage = fresh && fresh.lastMessage
    if (!lastMessage) return

    // Patch ONLY lastMessage on the matching row — keep the row's group/users/type
    // (the single-conversation endpoint returns `conversationWith`, not those). Skip if
    // the row is gone (list re-filtered) or already carries this exact message.
    const list = getRoot().conversationExplorer.conversationList
    const idx = list.findIndex(c => c.conversationId === conversationId)
    if (idx < 0) return
    if (list[idx].lastMessage && list[idx].lastMessage.id === lastMessage.id) return

    const next = list.slice()
    next[idx] = { ...list[idx], lastMessage }
    yield put({ type: actions.SET_STATE, payload: { conversationList: next } })
  } catch (error) {
    // Non-critical enrichment — a failure just leaves the existing (possibly stale)
    // preview in place; never surface an error for it.
  }
}

export default function* conversationExplorerSaga() {
  yield all([
    takeLatest(actions.LIST_CONVERSATIONS, LIST_CONVERSATIONS as SagaWorker),
    takeLatest(actions.SELECT_CONVERSATION, SELECT_CONVERSATION as SagaWorker),
    takeEvery(actions.LIST_MESSAGES, LIST_MESSAGES as SagaWorker),
    // Search replaces the thread — latest-wins so a stale response can't clobber it.
    takeLatest(actions.SEARCH_MESSAGES, LIST_MESSAGES as SagaWorker),
    takeEvery(actions.SELECT_MESSAGE, SELECT_MESSAGE as SagaWorker),
    takeLatest(actions.FETCH_GROUP_MEMBERS, FETCH_GROUP_MEMBERS as SagaWorker),
    takeLatest(actions.FETCH_FLAGGED_MESSAGES, FETCH_FLAGGED_MESSAGES as SagaWorker),
    takeLatest(actions.FETCH_MESSAGE_REACTIONS, FETCH_MESSAGE_REACTIONS as SagaWorker),
    takeLatest(actions.FETCH_MESSAGE_RECEIPTS, FETCH_MESSAGE_RECEIPTS as SagaWorker),
    takeLatest(actions.OPEN_AT_MESSAGE, OPEN_AT_MESSAGE as SagaWorker),
    takeEvery(actions.FETCH_MESSAGE_THREAD, FETCH_MESSAGE_THREAD as SagaWorker),
    takeEvery(actions.REFRESH_CONVERSATION_ROW, REFRESH_CONVERSATION_ROW as SagaWorker),
  ])
}
